import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { createAuthSlice, type AuthSlice } from './slices/authSlice';
import { createUiSlice, type UiSlice } from './slices/uiSlice';
import { createFiltersSlice, type FiltersSlice } from './slices/filtersSlice';
import { createPreferencesSlice, type PreferencesSlice } from './slices/preferencesSlice';

/**
 * App State - Combinaison de toutes les slices
 */
export type AppState = AuthSlice & UiSlice & FiltersSlice & PreferencesSlice;

/**
 * Store Zustand principal avec toutes les slices modulaires
 *
 * Architecture:
 * - AuthSlice: Authentification et session
 * - UiSlice: État UI (modals, sidebar, mobile menu)
 * - FiltersSlice: Filtres globaux (projets, factures, utilisateurs)
 * - PreferencesSlice: Préférences utilisateur
 */
export const useAppStore = create<AppState>()(
  devtools(
    (...a) => ({
      ...createAuthSlice(...a),
      ...createUiSlice(...a),
      ...createFiltersSlice(...a),
      ...createPreferencesSlice(...a),
    }),
    {
      name: 'AppStore',
      enabled: import.meta.env.DEV,
    }
  )
);

/**
 * Selectors optimisés avec useShallow pour éviter les re-renders inutiles
 * PERF-003: Utilisation de useShallow pour la comparaison superficielle
 */
export const useAuth = () => useAppStore(
  useShallow((state) => ({
    session: state.session,
    profile: state.profile,
    loading: state.loading,
    setSession: state.setSession,
    setProfile: state.setProfile,
    setLoading: state.setLoading,
    signOut: state.signOut,
  }))
);

export const useUi = () => useAppStore(
  useShallow((state) => ({
    openModals: state.openModals,
    openModal: state.openModal,
    closeModal: state.closeModal,
    closeAllModals: state.closeAllModals,
    isModalOpen: state.isModalOpen,
    sidebarExpanded: state.sidebarExpanded,
    toggleSidebar: state.toggleSidebar,
    setSidebarExpanded: state.setSidebarExpanded,
    mobileMenuOpen: state.mobileMenuOpen,
    toggleMobileMenu: state.toggleMobileMenu,
    setMobileMenuOpen: state.setMobileMenuOpen,
  }))
);

export const useFilters = () => useAppStore(
  useShallow((state) => ({
    projectFilters: state.projectFilters,
    setProjectFilters: state.setProjectFilters,
    resetProjectFilters: state.resetProjectFilters,
    invoiceFilters: state.invoiceFilters,
    setInvoiceFilters: state.setInvoiceFilters,
    resetInvoiceFilters: state.resetInvoiceFilters,
    userFilters: state.userFilters,
    setUserFilters: state.setUserFilters,
    resetUserFilters: state.resetUserFilters,
    resetAllFilters: state.resetAllFilters,
  }))
);

export const usePreferences = () => useAppStore(
  useShallow((state) => ({
    defaultView: state.defaultView,
    setDefaultView: state.setDefaultView,
    itemsPerPage: state.itemsPerPage,
    setItemsPerPage: state.setItemsPerPage,
    theme: state.theme,
    setTheme: state.setTheme,
    language: state.language,
    setLanguage: state.setLanguage,
    resetPreferences: state.resetPreferences,
  }))
);
